import React from 'react';
import { COCKPIT_DOMAINS } from './cockpit-data.jsx';
import { Icon } from './icons.jsx';
// Cockpit: Tab Domínios e Tema

// ── DomainCard ────────────────────────────────────────────────────────────────
const DomainCard = ({ domain, isPrimary, onToast }) => {
  const dnsOk = isPrimary ? true : domain.dnsVerified;

  return (
    <div className="card">
      <div className="card-head">
        <Icon name="globe" size={15} style={{ color: 'var(--text-muted)' }} />
        <h3>{isPrimary ? 'Domínio principal' : 'Domínio customizado'}</h3>
        {isPrimary && (
          <span className="badge success" style={{ fontSize: 10 }}><span className="dot" />Principal</span>
        )}
      </div>
      <div style={{ padding: '16px 20px' }}>
        {/* URL */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--bg-subtle)', borderRadius: 8, marginBottom: 14 }}>
          <Icon name="link" size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 13, flex: 1 }}>{domain.url}</span>
          <button className="icon-action" title="Copiar URL" onClick={() => { navigator.clipboard?.writeText('https://' + domain.url); onToast('URL copiada'); }}>
            <Icon name="copy" size={14} />
          </button>
          <a href={'https://' + domain.url} target="_blank" rel="noreferrer">
            <button className="icon-action" title="Abrir loja"><Icon name="externalLink" size={14} /></button>
          </a>
        </div>

        {/* Status grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
          {[
            { label: 'SSL / HTTPS', value: domain.ssl ? 'Ativo' : 'Inativo', ok: domain.ssl },
            { label: 'Status DNS',  value: isPrimary ? 'Propagado' : (dnsOk ? 'Verificado' : domain.dnsStatus), ok: dnsOk },
            ...(isPrimary ? [
              { label: 'Site ID',   value: `#${domain.siteId}`,     ok: true },
              { label: 'Site name', value: domain.siteName,          ok: true },
            ] : []),
          ].map((row, i) => (
            <div key={i} style={{ padding: '10px 12px', background: 'var(--bg-subtle)', borderRadius: 7 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 500, marginBottom: 4 }}>{row.label}</div>
              <div style={{ fontSize: 13, fontWeight: 500, color: row.ok ? 'var(--text)' : 'var(--warning)', display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: row.ok ? 'var(--success)' : 'var(--warning)', flexShrink: 0 }} />
                {row.value}
              </div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!isPrimary && !dnsOk && (
            <button className="btn sm" onClick={() => onToast('Validação DNS iniciada — aguarde propagação')}>
              <Icon name="refresh" size={13} />Validar DNS
            </button>
          )}
          {!isPrimary && (
            <button className="btn sm" onClick={() => onToast('Domínio definido como principal')}>
              <Icon name="check" size={13} />Tornar principal
            </button>
          )}
          {!isPrimary && (
            <button className="btn sm danger-outline" onClick={() => onToast('Domínio customizado removido')}>
              <Icon name="trash" size={13} />Remover
            </button>
          )}
          <button className="btn sm" onClick={() => window.open('https://' + domain.url, '_blank')}>
            <Icon name="externalLink" size={13} />Abrir loja
          </button>
        </div>
      </div>
    </div>
  );
};

// ── ThemeCard ─────────────────────────────────────────────────────────────────
const ThemeCard = ({ domains, onToast }) => (
  <div className="card">
    <div className="card-head">
      <Icon name="layers" size={15} style={{ color: 'var(--text-muted)' }} />
      <h3>Tema e aparência</h3>
    </div>
    <div style={{ padding: '16px 20px' }}>
      {/* Active theme */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', border: '1.5px solid var(--brand-navy)', borderRadius: 10, marginBottom: 14, background: 'color-mix(in srgb, var(--brand-navy) 4%, var(--bg-elevated))' }}>
        <div style={{ width: 44, height: 44, borderRadius: 8, background: 'var(--brand-navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name="layers" size={20} style={{ color: 'white' }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{domains.activeTheme}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Versão {domains.themeVersion} · Tema ativo</div>
        </div>
        <span className="badge success"><span className="dot" />Publicado</span>
      </div>

      {/* Available themes */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 10 }}>Temas liberados ({domains.availableThemes.length})</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {domains.availableThemes.map((t, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 7, background: t === domains.activeTheme ? 'var(--bg-subtle)' : 'transparent', border: '1px solid var(--border)' }}>
              <div style={{ width: 26, height: 26, borderRadius: 5, background: ['#1B3D60', '#111', '#8458b3'][i % 3], flexShrink: 0 }} />
              <span style={{ fontSize: 13, flex: 1 }}>{t}</span>
              {t === domains.activeTheme
                ? <span style={{ fontSize: 11, color: 'var(--success)', fontWeight: 600 }}>Ativo</span>
                : <button className="btn ghost sm" style={{ fontSize: 11, height: 26, padding: '0 8px' }} onClick={() => onToast(`Tema "${t}" aplicado`)}>Aplicar</button>}
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn sm" onClick={() => onToast('Tema reaplicado com sucesso')}>
          <Icon name="refresh" size={13} />Reaplicar tema
        </button>
        <button className="btn sm ghost" onClick={() => window.open('https://' + (cockpitData?.domains || COCKPIT_DOMAINS).primary.url, '_blank')}>
          <Icon name="externalLink" size={13} />Preview da loja
        </button>
      </div>
    </div>
  </div>
);

// ── CockpitDomainsTab ─────────────────────────────────────────────────────────
const CockpitDomainsTab = ({ cockpitData, onToast }) => (
  <div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
      <DomainCard domain={(cockpitData?.domains || COCKPIT_DOMAINS).primary} isPrimary={true}  onToast={onToast} />
      <DomainCard domain={(cockpitData?.domains || COCKPIT_DOMAINS).custom}  isPrimary={false} onToast={onToast} />
    </div>
    <ThemeCard domains={(cockpitData?.domains || COCKPIT_DOMAINS)} onToast={onToast} />
  </div>
);

export {
  CockpitDomainsTab,
};
